import type { Character } from '@/types/character';
import { migrate } from './serialize';

// Storage layout v2: a character library with an active selection. Older
// deployments stored a single character under `ars-magicka:current`; we
// migrate it into a one-item library on first load.
export interface LibraryEntry {
  id: string;
  char: Character;
}

export interface Library {
  characters: LibraryEntry[];
  activeId: string | null;
}

const LIBRARY_KEY = 'ars-magicka:library';
const LEGACY_KEY = 'ars-magicka:current';
const AUTOSAVE_DEBOUNCE_MS = 500;

export function genId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export function loadLibrary(): Library {
  try {
    const raw = localStorage.getItem(LIBRARY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.version === 2 && Array.isArray(parsed.characters)) {
        return {
          characters: parsed.characters
            .filter((e: unknown): e is { id: string; char: unknown } =>
              !!e && typeof e === 'object' && 'id' in e && 'char' in e
            )
            .map((e: { id: string; char: unknown }) => ({ id: e.id, char: migrate(e.char) })),
          activeId: typeof parsed.activeId === 'string' ? parsed.activeId : null,
        };
      }
    }
    // Migrate the legacy single-character slot.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const char = migrate(JSON.parse(legacy));
      const id = genId();
      const lib: Library = { characters: [{ id, char }], activeId: id };
      saveLibraryImmediate(lib);
      localStorage.removeItem(LEGACY_KEY);
      return lib;
    }
  } catch (err) {
    console.warn('[storage] load failed:', err);
  }
  return { characters: [], activeId: null };
}

function saveLibraryImmediate(lib: Library): void {
  try {
    localStorage.setItem(
      LIBRARY_KEY,
      JSON.stringify({ version: 2, characters: lib.characters, activeId: lib.activeId })
    );
  } catch (err) {
    console.warn('[storage] save failed:', err);
  }
}

let timer: number | undefined;
export function scheduleLibrarySave(lib: Library): void {
  if (timer) window.clearTimeout(timer);
  timer = window.setTimeout(() => saveLibraryImmediate(lib), AUTOSAVE_DEBOUNCE_MS);
}

export function clearLibrary(): void {
  localStorage.removeItem(LIBRARY_KEY);
}
